import { filterListByCurrentIndustryCode } from "../../Utils";
import { Footer } from "../../components/Footer";
import { Layout } from "../../components/Layout";
import { RelatedCaseStudies } from "../../components/RelatedCaseStudies";
import { useIsMobile } from "../../hooks/useIsMobile";
import { graphql } from "gatsby";
import * as React from "react";
import { Helmet } from "react-helmet";

export default function CaseStudy({ data, location }) {
  return (
    <Layout location={location}>
      <main id="main" className="marketing-content container-fluid">
        <Helmet
          bodyAttributes={{
            class: "body-shorthand body-marketing body-marketing-secondary body-marketing-case-studies hide-elevio",
          }}
        >
          <title>Rutgers ISGRJ | Shorthand Case Study</title>
          <meta name="description" content="Rutgers University Institute for the Study of Global Racial Justice." />
        </Helmet>
        <div className="row section-title">
          <div className="col-sm-12 col-md-10 col-lg-8 col-xl-6  mx-auto  section-inner">
            <h1>
              <span className="shorthand-logo">Shorthand</span>
              <span className="subsection-label">Case Study:</span>
              Rutgers University Institute for the Study of Global Racial Justice
            </h1>
            <h2>Using Shorthand, the ISGRJ amplifies important research to inform real world decisions on critical issues.</h2>
          </div>
          <div aria-hidden="true" className="background-media">
            <div className="tw-absolute tw-inset-0 tw-z-10 tw-bg-black tw-bg-opacity-65"></div>
            <img
              src="/media/case-studies/Rutgers_CaseStudy_Title_IMG.png"
              data-object-fit="cover"
              data-autoscale-content-ratio="1.77777777778"
              alt="Rutgers Institute for the Study of Global Racial Justice"
            />
          </div>
        </div>
        <div className="row section">
          <div className="col-sm-12 col-md-10 col-lg-8 col-xl-6  mx-auto marketing-main-column">
            <h3 className="intro tw-mb-5">
              The Rutgers Institute for the Study of Global Racial Justice connects brilliant minds doing leading work across three
              campuses and dozens of departments. Shorthand is the tool that helps elevate this critical work.
            </h3>
            <br />
            <div className="inline-image featured ">
              <img
                src="/media/case-studies/Rutgers_CaseStudy_Hero.gif"
                data-object-fit="cover"
                data-autoscale-content-ratio="1.77777777778"
                alt=""
              />
            </div>
            <p>
              Rutgers University spans the state of New Jersey, with campuses in Newark, Camden, and New Brunswick. It&apos;s a diverse
              institution with broad geographical and intellectual footprints.
            </p>
            <figure className="block quote-block offset right">
              <blockquote>
                <p>
                  &quot;I remember clicking on it and immediately being blown away. The artistry behind it. The engaging composition,
                  the images, the powerful content.&quot;
                </p>
              </blockquote>
              <figcaption className="quote-cite">Tania Bentley</figcaption>
            </figure>
            <p>
              Prior to the foundation of its Institute for the Study of Global Racial Justice, there was already much research and work
              on racial justice across the Rutgers network. But things were very &quot;siloed&quot;, says Tania Bentley, the
              Institute&apos;s director of marketing and communications. &quot;It was very rare that it reached the other parts of the
              university.&quot;
            </p>
            <p>
              The Institute was founded in 2020 to open up &quot;intellectual corridors&quot; between campuses, projects, students, and
              faculties.
            </p>
            <p>Storytelling is crucial to this work.</p>
            <h3 className="subheading">How storytelling sparks discussions and informs better decisions</h3>
            <p>
              The Institute&apos;s job, says Bentley, is to "Connect and elevate the research being conducted within the global racial
              justice space, and provide opportunities for Rutgers faculty researchers to work collaboratively.
            </p>
            <p>
              &quot;By elevating that research and those ideas, [storytelling] escalates the likelihood that those explorations will
              inform real world decisions and provide solutions to problems.&quot;
            </p>
            <p>
              Bentley is the main source of the Institute&apos;s output on Shorthand. She prepares features, summaries, and newsletters
              covering research, events, initiatives, and action, into engaging articles for all to see. One fantastic example is
              &apos;
              <a href="https://isgrjstories.rutgers.edu/black-bodies-black-health/index.html" target="_blank">
                Black Bodies Black Health
              </a>
              &apos;, a visual archive covering 18 months of research supported by the Robert Wood Johnson Foundation on racial health
              disparities.
            </p>
            <div className="photo-grid flex featured">
              <img
                className="col-12 col-sm-6"
                src="/media/case-studies/Rutgers_CaseStudy_BlackBodiesBlackHealth_1.png"
                alt="tripadvisor repor"
              />
              <img
                className="col-12 col-sm-6"
                src="/media/case-studies/Rutgers_CaseStudy_BlackBodiesBlackHealth_2.png"
                alt="rutgers case study"
              />
              <img
                className="col-12 col-sm-6"
                src="/media/case-studies/Rutgers_CaseStudy_BlackBodiesBlackHealth_3.png"
                alt="rutgers case study"
              />
              <img
                className="col-12 col-sm-6"
                src="/media/case-studies/Rutgers_CaseStudy_BlackBodiesBlackHealth_4.png"
                alt="rutgers case study"
              />
            </div>
            <p>
              &quot;I&apos;m <i>it</i>,&quot; she chuckles. The ability to work collaboratively with faculties and students, but to
              still have &quot;full editorial creative control&quot;, is one thing she loves about the job — &quot;the latitude to do
              the kind of storytelling that I am passionate about and am yearning to do.&quot;
            </p>
            <p>
              With a 15-year background in television, working at the public broadcasting program <i>Due Process</i>, she has a knack
              for visuals and a passion for social and racial justice. When she first saw Shorthand, the value was clear.
            </p>
            <p>Bentley spotted a story prepared with Shorthand published by a different department.</p>
            <figure className="block quote-block offset left">
              <blockquote>
                <p>
                  &quot;People are paying attention to us, they&apos;re paying attention to our researchers, our faculty, and
                  they&apos;re learning things about what&apos;s happening that they never would have had access to.&quot;
                </p>
              </blockquote>
              <figcaption className="quote-cite">Tania Bentley</figcaption>
            </figure>
            <p>
              &quot;I remember clicking on it and immediately being blown away,&quot; Bentley says. &quot;The artistry behind it. The
              engaging composition, the images, the powerful content.
            </p>
            <p>&quot;I immediately sent it to our executive director and said &apos;Whatever this is, we need to have this.&apos;</p>
            <p>
              &quot;We contacted the person that had written the story, and he said 'Oh, yeah, we use a great platform called
              Shorthand.&apos; It was immediate. We purchased our own plan and that&apos;s where I got to start being very, very
              creative.&quot;
            </p>
            <p>
              Today, Bentley says, &quot;What I love about it is I can expand on and use all of the various sections to create
              something that is really so powerful… I use the platform in such an extensive way that I reach the section limit every
              time!&quot;
            </p>
            <h3 className="subheading">The media and the message</h3>
            <p>
              &quot;A lot of what we do is tell the story about these efforts and this transformative research on race,&quot; she says.
              Good storytelling, and its importance to her institute and its work, is a constant theme throughout our conversation.
            </p>
            <p>
              &quot;When you found an institute like ours within an academic space, and from a human enquiry standpoint, it&apos;s
              difficult to say how it is that you're actually impacting change without the storytelling component.
            </p>
            <p>
              &quot;I&apos;m very video-based, picture-based, in the way I like to communicate. Text is the lowest on my radar. I
              always think about: 'How do we engage and tell stories in a way that keeps interest, but has impact?&apos;&quot;
            </p>
            <p>Bentley&apos;s approach to blending the visual with the message works.</p>
            <p>
              One of her earliest, biggest, and most memorable Shorthand stories was a 2022 newsletter. After two chaotic years of
              coronavirus, 2022 was the university&apos;s first year returning to a full calendar of programs, initiatives and events.
              It was a big year to summarise and reflect upon.
            </p>
            <p>Bentley put the newsletter together over months. And it received more than 1000 views upon its release.</p>
            <p>
              &quot;Within a state university that has so many multiple departments doing their own thing, 1000 is pretty
              sizable,&quot; Bentley says.
            </p>
            <p>
              Building on the success of the first newsletter, Bentley has continued to publish the newsletters annually, with Fall{" "}
              <a href="https://isgrjstories.rutgers.edu/fall-2024-Newsletter/" target="_blank">
                2024
              </a>{" "}
              being the most recent edition.
            </p>
            <div className="photo-grid flex featured">
              <img
                className="col-12 col-sm-6"
                src="/media/case-studies/Rutgers_CaseStudy_FallNewsletter_1.png"
                alt="tripadvisor repor"
              />
              <img
                className="col-12 col-sm-6"
                src="/media/case-studies/Rutgers_CaseStudy_FallNewsletter_2.png"
                alt="rutgers case study"
              />
              <img
                className="col-12 col-sm-6"
                src="/media/case-studies/Rutgers_CaseStudy_FallNewsletter_3.png"
                alt="rutgers case study"
              />
              <img
                className="col-12 col-sm-6"
                src="/media/case-studies/Rutgers_CaseStudy_FallNewsletter_4.png"
                alt="rutgers case study"
              />
            </div>
            <h3 className="subheading">Measuring impact </h3>
            <p>
              Bentley says it&apos;s difficult to measure ROI and impact in a site based on humanistic inquiry. &quot;There is no
              direct impact that we have on something. It&apos;s not like we&apos;re going and reforming in a way that is combating
              racism and fighting for equality, we&apos;re doing so from a research inquiry standpoint, and bringing together various
              researchers across the university.&quot;
            </p>
            <figure className="block quote-block offset right">
              <blockquote>
                <p>
                  &quot;The fact that we&apos;re getting as much engagement, as much visibility that has been increased over the years,
                  speaks to impact.&quot;
                </p>
              </blockquote>
              <figcaption className="quote-cite">Tania Bentley</figcaption>
            </figure>
            <p>
              Focusing on engagement and building connections leads to better, more well-informed conversations, and thus social
              outcomes.
            </p>
            <p>
              Bentley explains: &quot;People are paying attention to us, they&apos;re paying attention to our researchers, our faculty,
              and they&apos;re learning things about what&apos;s happening that they never would have had access to.&quot;
            </p>
            <p>
              <p>She feels the momentum will only increase.</p>
              <p>
                &quot;We are still fairly new as an institute, so those stats and those performance measurables speak for themselves.
                Especially because we&apos;re competing with an entire university.
              </p>
              <p>
                &quot;The fact that we&apos;re getting as much engagement, as much visibility that has been increased over the years
                speaks to impact.&quot;
              </p>
              <h3 className="subheading">Getting it right on Shorthand</h3>
              <p>
                Bentley has just one word for other creators on Shorthand. Well, three words. &quot;Collaborate, collaborate,
                collaborate.
              </p>
              <figure className="block quote-block offset left">
                <blockquote>
                  <p>
                    &quot;I&apos;m constantly being asked to jump on a call with someone else in another department going 'Hey, how did
                    you do <i>this</i>?&apos;&quot;
                  </p>
                </blockquote>
                <figcaption className="quote-cite">Tania Bentley</figcaption>
              </figure>
              <p>&quot;As much as you can.</p>
              <p>
                &quot;Reach out to other designers, other communications specialists, who are using not just this platform but
                others.&quot;
              </p>
              This is the key for creativity that drives creativity. &quot;[It&apos;s] the entire mission of our Institute, you work in
              a silo, you never know what is possible, what others are doing, so you share, reach out, collaborate.
            </p>{" "}
            <p>
              &quot;I take a lot of time going through what someone else has done, even if it&apos;s something that&apos;s completely
              corporate or has nothing to do with academia, or has nothing to do with research, or nothing to do with whatever it is
              that we&apos;re working on, and see the ways that they&apos;ve creatively used [Shorthand].&quot;
            </p>
            <div className="photo-grid flex featured">
              <img
                className="col-12 col-sm-6"
                src="/media/case-studies/Rutgers_CaseStudy_Scholarship_1.png"
                alt="rutgers case study"
              />
              <img
                className="col-12 col-sm-6"
                src="/media/case-studies/Rutgers_CaseStudy_Scholarship_2.png"
                alt="rutgers case study"
              />
              <img
                className="col-12 col-sm-6"
                src="/media/case-studies/Rutgers_CaseStudy_Scholarship_3.png"
                alt="rutgers case study"
              />
              <img
                className="col-12 col-sm-6"
                src="/media/case-studies/Rutgers_CaseStudy_Scholarship_4.png"
                alt="rutgers case study"
              />
            </div>
            <div className="caption">
              A selection of images from 'ISGRJ and Sponsored Research Projects at Rutgers University&apos;
            </div>
            <h3 className="subheading">The power of stories in an era of uncertainty</h3>
            <p>
              The world is in an era of flux and division, what Bentley calls a &quot;heightened&quot; state, similar to that which
              necessitated the BLM movement, and the Institute itself, in 2020. She&apos;s certain that the Institute&apos;s work has
              never been so necessary. &quot;And that's how we do it, through this research, through this inquiry, through advancing
              what it is that people are working on.&quot;
            </p>
            <p>As for Bentley herself, her Shorthand skills are in hot demand from colleagues and other departments.</p>
            <p>
              &quot;We kind of started a chain reaction,&quot; she says. &quot;Because it was our colleagues in Camden that had it
              first, then we had it, and then somebody saw it and went 'Wait a minute, what&apos;s that?&apos;
            </p>
            <p>
              &quot;So I&apos;m constantly being asked to jump on a call with someone else in another department going 'Hey, how did
              you do <i>this</i>?&apos;&quot;
            </p>
          </div>
        </div>
        <RelatedCaseStudies studies={filterListByCurrentIndustryCode(data, "rutgers-isgrj")} />
        <Footer />
      </main>
    </Layout>
  );
}

export const query = graphql`
  query RelxQuery {
    allCaseStudiesJson {
      edges {
        node {
          jsonId
          image
          industry
          industryCode
          organisation
          summary
        }
      }
    }
  }
`;
